import { useState } from 'react';
import { clsx } from 'clsx';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

import { toast } from '../../Toast';
import { Info } from '../../Info/Info';
import { Input } from '../../Input/Input';
import { Table } from '../../Table/Table';
import type { TableColumn } from '../../Table/Table.types';
import { useMasterDocumentFormatters } from '../formatters';

import { InstallmentManualBadge } from './InstallmentManualBadge';
import { RegenerateInstallmentsModal } from './RegenerateInstallmentsModal';
import { useInstallmentsTranslation } from './i18n';
import { useInstallments, useModifyInstallment, useRegenerateInstallments } from './hooks';
import type { InstallmentDto, InstallmentsHttpError, InstallmentsPanelProps } from './types';

const DASH = '—';

/**
 * Panel de Cuotas (TF2) — sub-componente de MasterDocument (FEAT-105-7).
 *
 * Renderiza el plan de cuotas del documento en una grilla (número, vencimiento,
 * fecha de pronto pago, monto, descuento PP y badge "Manual"). Cuando el usuario
 * tiene el permiso `alter_installments` y el documento es editable (DRAFT):
 * permite editar el monto in-line (`PUT /{id}/installments/{installmentId}`) y
 * regenerar el plan completo (`POST /{id}/installments/regenerate`).
 *
 * Diseñado para montarse en una sección FactBox de cualquier documento
 * MasterDocument, inyectando sus endpoints e IO por props (precedente:
 * AdvanceApplicationsPanel / SerialPanel).
 *
 * @example
 * ```tsx
 * <InstallmentsPanel
 *   documentId={docId}
 *   isEditable={docState === 'DRAFT'}
 *   canAlter={permissions.includes(CORE_PERMISSIONS.SUPPLIER_INVOICES.ALTER_INSTALLMENTS)}
 *   installmentsEndpoint={`/api/v1/purchases/supplier-invoices/${docId}/factbox/installments`}
 *   modifyInstallmentEndpoint={`/api/v1/purchases/supplier-invoices/${docId}/installments`}
 *   regenerateEndpoint={`/api/v1/purchases/supplier-invoices/${docId}/installments/regenerate`}
 *   httpRequest={httpRequest}
 * />
 * ```
 */
export function InstallmentsPanel({
  documentId,
  isEditable,
  canAlter,
  installmentsEndpoint,
  modifyInstallmentEndpoint,
  regenerateEndpoint,
  httpRequest,
  initialInstallments,
  formatAmount: formatAmountProp,
  formatDate: formatDateProp,
  className,
}: InstallmentsPanelProps) {
  const { t } = useInstallmentsTranslation();
  const { formatAmount: defaultFormatAmount, formatDate: defaultFormatDate } =
    useMasterDocumentFormatters();
  const formatAmount = formatAmountProp ?? defaultFormatAmount;
  const formatDate = formatDateProp ?? defaultFormatDate;

  /** La edición solo procede con permiso + documento editable (gating FE de G3). */
  const editableNow = canAlter && isEditable;

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [pendingId, setPendingId] = useState<string | null>(null);

  const installmentsQuery = useInstallments({
    documentId,
    installmentsEndpoint,
    httpRequest,
    initialData: initialInstallments,
  });

  const modifyMutation = useModifyInstallment({
    documentId,
    modifyInstallmentEndpoint,
    httpRequest,
  });

  const regenerateMutation = useRegenerateInstallments({
    documentId,
    regenerateEndpoint,
    httpRequest,
  });

  const installments = installmentsQuery.data ?? [];

  function cancelEdit() {
    setEditingId(null);
    setEditValue('');
  }

  function startEdit(row: InstallmentDto) {
    if (!editableNow || pendingId) return;
    setEditingId(row.id);
    setEditValue(String(row.amount ?? 0));
  }

  function reportError(error: unknown) {
    const status = (error as InstallmentsHttpError)?.status;
    if (status === 403) {
      toast.error(t('installments.error.forbidden'));
      return;
    }
    if (status === 409) {
      toast.error(t('installments.error.conflict'));
      return;
    }
    if (status === 422) {
      toast.error(t('installments.error.tooMany'));
      return;
    }
    toast.error((error as Error)?.message || t('installments.error.generic'));
  }

  function commitEdit(row: InstallmentDto) {
    const next = Number(editValue);
    cancelEdit();

    // Sin cambio o valor inválido → no se muta.
    if (editValue.trim() === '' || Number.isNaN(next) || next === row.amount) {
      return;
    }
    if (next < 0) {
      toast.error(t('installments.error.amountPositive'));
      return;
    }

    setPendingId(row.id);
    modifyMutation.mutate(
      { installmentId: row.id, payload: { rowVersion: row.rowVersion, amount: next } },
      {
        onSuccess: () => toast.success(t('installments.toast.updated')),
        onError: reportError,
        onSettled: () => setPendingId(null),
      },
    );
  }

  function handleRegenerate() {
    regenerateMutation.mutate(undefined, {
      onSuccess: () => {
        toast.success(t('installments.toast.regenerated'));
        setModalOpen(false);
      },
      onError: reportError,
    });
  }

  const columns: TableColumn<InstallmentDto>[] = [
    {
      header: t('installments.column.number'),
      accessor: 'installmentNumber',
      align: 'center',
      width: '72px',
    },
    {
      header: t('installments.column.dueDate'),
      accessor: 'dueDate',
      render: (value) => (value ? formatDate(value as string) : DASH),
    },
    {
      header: t('installments.column.promptPaymentDate'),
      accessor: 'promptPaymentDate',
      render: (value) => (value ? formatDate(value as string) : DASH),
    },
    {
      header: t('installments.column.amount'),
      accessor: 'amount',
      align: 'right',
      render: (_value, row) => {
        if (editingId === row.id) {
          return (
            <Input
              type="number"
              min={0}
              step="any"
              inputSize="xs"
              autoFocus
              value={editValue}
              aria-label={t('installments.edit.amountAria', { number: row.installmentNumber })}
              onChange={(e) => setEditValue(e.target.value)}
              onBlur={() => commitEdit(row)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  commitEdit(row);
                } else if (e.key === 'Escape') {
                  e.preventDefault();
                  cancelEdit();
                }
              }}
              className="w-28 ml-auto"
            />
          );
        }
        return (
          <span
            className={clsx(
              'inline-flex items-center gap-1.5 justify-end tabular-nums',
              editableNow && !pendingId && 'cursor-pointer hover:underline',
              pendingId === row.id && 'opacity-50',
            )}
            onDoubleClick={() => startEdit(row)}
            title={editableNow ? t('installments.edit.amountAria', { number: row.installmentNumber }) : undefined}
          >
            {formatAmount(row.amount)}
          </span>
        );
      },
    },
    {
      header: t('installments.column.promptPaymentDiscount'),
      accessor: 'promptPaymentDiscount',
      align: 'right',
      render: (value) =>
        value == null ? DASH : <span className="tabular-nums">{formatAmount(value as number)}</span>,
    },
    {
      header: t('installments.column.flags'),
      accessor: 'isManual',
      align: 'center',
      width: '84px',
      render: (_value, row) =>
        row.isManual ? (
          <InstallmentManualBadge
            autoAmountLabel={
              typeof row.autoGeneratedSnapshot?.amount === 'number'
                ? formatAmount(row.autoGeneratedSnapshot.amount)
                : undefined
            }
          />
        ) : null,
    },
  ];

  return (
    <section
      className={clsx('flex flex-col gap-3', className)}
      aria-label={t('installments.panel.title')}
    >
      {/* Header */}
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-bold text-content-primary dark:text-dark-content-primary truncate">
          {t('installments.panel.title')}
        </h3>

        {/* Botón Regenerar — solo en DOM cuando editableNow (oculto, no deshabilitado) */}
        {editableNow && (
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            disabled={regenerateMutation.isPending}
            aria-label={t('installments.panel.regenerateAria')}
            className={clsx(
              'inline-flex items-center gap-1.5 shrink-0',
              'px-3 py-1.5 rounded-md text-xs font-semibold',
              'bg-primary-600 text-white',
              'hover:bg-primary-700 transition-colors duration-150',
              'focus:outline-hidden focus:ring-2 focus:ring-primary-400 focus:ring-offset-2',
              'dark:bg-dark-bg-inverse dark:text-dark-content-inverse dark:hover:bg-dark-bg-inverse/90',
              'dark:focus:ring-dark-border-custom dark:focus:ring-offset-dark-bg-primary',
              'disabled:opacity-40 disabled:pointer-events-none',
            )}
          >
            <ArrowPathIcon className="w-4 h-4" aria-hidden="true" />
            {t('installments.panel.regenerate')}
          </button>
        )}
      </div>

      {/* Estados de carga / error / grilla */}
      {installmentsQuery.isError ? (
        <Info color="red" title={t('installments.panel.loadError')} />
      ) : (
        <Table<InstallmentDto>
          columns={columns}
          data={installments}
          variant="basic"
          fullWidth
          showShadow={false}
          loading={installmentsQuery.isLoading}
          emptyMessage={t('installments.panel.empty')}
        />
      )}

      {/* Modal de regeneración (solo se monta si editableNow) */}
      {editableNow && (
        <RegenerateInstallmentsModal
          open={modalOpen}
          isProcessing={regenerateMutation.isPending}
          onConfirm={handleRegenerate}
          onClose={() => setModalOpen(false)}
        />
      )}
    </section>
  );
}
